import type { TooltipProps } from 'recharts'
import type {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent'
import CompoundingYieldIcon from '~/@mid_ui/icons/CompoundingYieldIcon'
import { cn } from '~/shadcn/lib/utils'

type Props = TooltipProps<ValueType, NameType> & {
  payload: any
  label: any
  variant: 'black' | 'white'
}

export function ChartTooltip({
  active,
  payload,
  label,
  variant = 'black',
}: Props) {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    const simpleValue = data['Mesačne vyplácané úroky %']
    const compoundValue = data['Rastúce mesačné úročenie']
    const currentCompoundedYieldPerAnnumAverage =
      data['currentCompoundedYieldPerAnnumAverage']

    const tooltipClasses = (function () {
      if (variant === 'black') {
        return {
          tooltipBg: 'bg-black',
          fontGray: 'text-gray-500',
          bgGray: 'bg-gray-800',
          fontSuccessGray: 'text-gray-100',
          fontSizeSuccess: 'text-2xl',
          fontSuccess: 'text-[#50F000]',
          bgGreen: 'bg-[#003300]',
        }
      }
      return {
        tooltipBg: 'bg-background',
        fontGray: 'text-gray-600',
        bgGray: 'bg-gray-200',
        fontSuccessGray: 'text-gray-900',
        fontSizeSuccess: 'text-2xl',
        fontSuccess: 'text-[#50F000]',
        bgGreen: 'bg-[#E3FFEA]',
      }
    })()

    return (
      <div
        translate="no"
        className={cn(
          tooltipClasses.tooltipBg,
          'p-2 shadow-lg border-none rounded-xl min-w-[250px]'
        )}>
        <div className="flex flex-row justify-between items-center mb-0.5">
          <p className={cn('text-xs text-gray-500 block mb-1')}>
            {calculateFutureDate(label)}
          </p>
          <div className="flex flex-row items-center gap-1">
            <p className={cn('text-xs flex flex-row text-gray-500 gap-1 mb-1')}>
              {`${currentCompoundedYieldPerAnnumAverage} `}
              <CompoundingYieldIcon
                arrowColor="#9ca3af"
                percentColor="#9ca3af"
                size={18}
              />
            </p>
            <p className={cn(tooltipClasses.fontGray, 'text-xs')}>
              <sup>1</sup>
            </p>
          </div>
        </div>

        <div
          className={cn(
            'flex flex-row justify-between items-center mb-0.5',
            'border-l-[3px] border-l-solid border-l-[#50F000] pl-2'
          )}>
          <p
            className={cn(
              'font-bold',
              tooltipClasses.fontSuccessGray,
              tooltipClasses.fontSizeSuccess
            )}>
            {compoundValue.toLocaleString('sk-SK', {
              style: 'currency',
              currency: 'EUR',
            })}
          </p>
          <div className="flex flex-row items-center gap-1">
            <p
              className={cn(
                'text-sm font-semibold',
                tooltipClasses.fontSuccess,
                tooltipClasses.bgGreen,
                'px-1.5 py-0.5 rounded'
              )}>
              +{data.compoundRoi.toString().replace('.', ',')}%
            </p>
            <p className={cn(tooltipClasses.fontGray, 'text-xs')}>
              <sup>2</sup>
            </p>
          </div>
        </div>

        <div
          className={cn(
            'flex flex-row justify-between items-center',
            'border-l-[3px] border-l-dashed border-l-gray-400 pl-2'
          )}>
          <p className={cn('text-sm', tooltipClasses.fontGray)}>
            {simpleValue.toLocaleString('sk-SK', {
              style: 'currency',
              currency: 'EUR',
            })}
          </p>
          <div className="flex flex-row items-center gap-1">
            <p
              className={cn(
                'text-sm font-semibold',
                tooltipClasses.fontGray,
                tooltipClasses.bgGray,
                'px-1.5 py-0.5 rounded'
              )}>
              +{data.simpleRoi.toString().replace('.', ',')}%
            </p>
            <p className={cn(tooltipClasses.fontGray, 'text-xs')}>
              <sup>3</sup>
            </p>
          </div>
        </div>
      </div>
    )
  }
  return null
}

function calculateFutureDate(monthsToAdd: number): string {
  const date = new Date()
  date.setMonth(date.getMonth() + monthsToAdd)
  return `${date.getDate()} ${date.toLocaleString('en-US', { month: 'short' }).toUpperCase()}`
}
